import { useState } from 'react';
import { Box, Typography, Grid, Paper, Avatar, CircularProgress } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BuildIcon from '@mui/icons-material/Build';
import PeopleIcon from '@mui/icons-material/People';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SpeedIcon from '@mui/icons-material/Speed';

import VehicleStatusDialog from './VehicleStatusDialog';
import MasterVehicleDialog from './MasterVehicleDialog';
import DriverDialog from './DriverDialog';
import RitaseDialog from './RitaseDialog';
import { useLanguage } from '../../../utils/i18n';

export default function SummaryAsset({ data, loading, periodType = 'today' }) {
  const { t } = useLanguage();
  const [modalOpen, setModalOpen] = useState(null);

  const getPeriodLabel = () => {
    switch (periodType) {
      case 'today':
        return t('tireRepairScrap.today', 'Today');
      case 'last_24_hours':
        return t('dashboardFuel.last24Hours', 'Last 24 Hours');
      case 'last_week':
        return t('mapsTracking.lastWeek', 'Last Week');
      case 'last_month':
        return t('reportFatigue.lastMonth', 'Last Month');
      case 'custom':
        return t('reportFatigue.custom', 'Custom');
      default:
        return t('tireRepairScrap.today', 'Today');
    }
  };
  const periodLabel = getPeriodLabel();

  const avgRitase = data?.totalRitaseDrivers > 0 ? (data.totalRitase / data.totalRitaseDrivers).toFixed(1) : 0;

  const handleCardClick = (modalType) => {
    setModalOpen(modalType);
  };

  const closeModal = () => {
    setModalOpen(null);
  };

  const statCardProps = {
    variant: 'outlined',
    sx: {
      p: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: 2,
      borderWidth: 2,
      boxShadow: 2,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 4,
        borderColor: '#1976d2'
      }
    }
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: '#ffffff', boxShadow: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            bgcolor: '#4c5c96',
            color: 'white',
            borderRadius: 1.5,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <TrendingUpIcon fontSize="small" />
        </Box>
        <Typography variant="h4" fontWeight="bold">
          {t('dashboardAsset.summaryAsset', 'Summary Asset')}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper {...statCardProps} onClick={() => handleCardClick('master_vehicle')}>
            <Box>
              <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
                {t('dashboardAsset.totalVehicles', 'Total Vehicles')}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : data?.totalVehicle || 0}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#1976d2', color: 'white', width: 40, height: 40 }}>
              <DirectionsCarIcon />
            </Avatar>
          </Paper>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper {...statCardProps} onClick={() => handleCardClick('gps_update')}>
            <Box>
              <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
                {t('dashboardAsset.activeVehicles', 'Active Vehicles')}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : data?.gpsUpdateVehicles?.length || 0}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#01FF05', color: 'white', width: 40, height: 40 }}>
              <CheckCircleOutlineIcon />
            </Avatar>
          </Paper>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper {...statCardProps} onClick={() => handleCardClick('maintenance')}>
            <Box>
              <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
                {t('dashboardAsset.maintenance', 'Maintenance')}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : data?.totalVehicleMaintenance || 0}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#FCB859', color: 'white', width: 40, height: 40 }}>
              <BuildIcon />
            </Avatar>
          </Paper>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper {...statCardProps} onClick={() => handleCardClick('active_driver')}>
            <Box>
              <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
                {t('dashboardAsset.totalActiveDrivers', 'Total Active Drivers')}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : data?.totalActiveDriver || 0}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#1976d2', color: 'white', width: 40, height: 40 }}>
              <PeopleIcon />
            </Avatar>
          </Paper>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper {...statCardProps} onClick={() => handleCardClick('ritase')}>
            <Box>
              <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
                {t('revenue.totalRetase', 'Total Ritase')} {periodLabel}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : data?.totalRitase || 0}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#1976d2', color: 'white', width: 40, height: 40 }}>
              <LocalShippingIcon />
            </Avatar>
          </Paper>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 4 }}>
          <Paper {...statCardProps} onClick={() => handleCardClick('ritase_avg')}>
            <Box>
              <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
                {t('dashboardAsset.avgRitaseDriver', 'Average Ritase/Driver')}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {loading ? <CircularProgress size={24} color="inherit" /> : avgRitase}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#1976d2', color: 'white', width: 40, height: 40 }}>
              <SpeedIcon />
            </Avatar>
          </Paper>
        </Grid>
      </Grid>

      <MasterVehicleDialog open={modalOpen === 'master_vehicle'} onClose={closeModal} data={data?.masterVehicles || []} />
      <VehicleStatusDialog
        open={modalOpen === 'gps_update'}
        onClose={closeModal}
        title={t('dashboardAsset.activeVehicles', 'Active Vehicles')}
        data={data?.gpsUpdateVehicles || []}
      />
      <VehicleStatusDialog
        open={modalOpen === 'maintenance'}
        onClose={closeModal}
        title={t('dashboardAsset.maintenance', 'Maintenance')}
        data={data?.maintenanceVehicles || []}
      />
      <DriverDialog open={modalOpen === 'active_driver'} onClose={closeModal} data={data?.activeDrivers || []} />
      <RitaseDialog
        open={modalOpen === 'ritase' || modalOpen === 'ritase_avg'}
        onClose={closeModal}
        data={data?.ritaseTrips || []}
        showDriver={modalOpen === 'ritase_avg'}
      />
    </Paper>
  );
}
